import { __ } from '../../utils';
// `Chip` z knižnice je od v2 chip na viacnásobný výber (`children`,
// `selected`, `onRemove`), nie MUI štítok. Tu ide vedome MUI komponent:
// značky vo `renderValue` dostávajú od MUI `Autocomplete`-u `getItemProps()` - `className`, `tabIndex`, `data-item-index`, `onDelete` - a tým si riadi fokus medzi značkami.
// Prepis na `StatusChip` / `FilterChip` patrí k prerobeniu tejto obrazovky
// na `DataTable` a `FilterBar`, nie k výmene shellu.
import MuiChip from '@mui/material/Chip';
import {
  Autocomplete,
  CircularProgress,
  ErrorMark,
  Field as FieldShell,
  TextField,
} from '@presov.sk/presov-ui-react';
import type { FieldValidator } from 'final-form';
import React from 'react';
import { useField } from 'react-final-form';

type Option = {
  label: string;
  value: string;
};

type Props = {
  name: string;
  label?: string;
  fullWidth?: boolean;
  options: Option[];
  required?: boolean;
  multiple?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validate?: FieldValidator<any>;
  info?: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  onInputChange?: (
    event: React.ChangeEvent,
    value: string,
    reason: string,
  ) => void;
  noOptionsText?: string;
  freeSolo?: boolean;
  /**
   * Možnosti prichádzajú z API pre aktuálny `onInputChange` (server už filtroval),
   * takže druhé, klientske filtrovanie ich len zahodí - porovnávalo by vstup so
   * `label`, a čo `label` neobsahuje (e-mail, `acc_id`, iné poradie slov), by
   * z ponuky vypadlo, hoci to server našiel.
   */
  disableClientFilter?: boolean;
  /**
   * Dopĺňanie na scroll. `onLoadMore` sa zavolá, keď sa ponuka doscrolluje na
   * konec; `hasMore` hovorí, či je čo dopĺňať. Matematika scrollu je tu a nie
   * u volajúceho zámerne - je to vlastnosť tohto poľa, nie obrazovky.
   */
  onLoadMore?: () => void;
  hasMore?: boolean;
};

/**
 * Toto nie je prihlasovacie pole a správcovia hesiel to majú nechať na pokoji.
 * `autocomplete='off'`, ktoré dáva MUI samo, na to nestačí - nad poľom
 * s „user" v `name`/`id` vyskočí napríklad rozšírenie iCloud Passwords s ponukou
 * účtov (dialóg „Pridanie používateľa do kategórie"). Preto:
 * `type='search'` - hľadacie polia správcovia neponúkajú,
 * `data-*` - vypínače 1Password, LastPass, Bitwarden a Dashlane,
 * a vstup zámerne nedostáva `name`: v DOM ho netreba (hodnotu drží final-form)
 * a je to prvé, čoho sa heuristika chytá.
 */
const searchInputProps = {
  type: 'search',
  'data-1p-ignore': true,
  'data-lpignore': 'true',
  'data-bwignore': true,
  'data-form-type': 'other',
} as React.InputHTMLAttributes<HTMLInputElement>;

const FormAutocompleteField: React.FC<Props> = ({
  name,
  label,
  fullWidth = true,
  validate,
  required = false,
  multiple,
  options,
  info,
  loading,
  disabled = false,
  freeSolo = false,
  onInputChange,
  noOptionsText = __('Žiadne možnosti'),
  disableClientFilter = false,
  onLoadMore,
  hasMore = false,
}) => {
  const {
    input: { value, onChange, onBlur },
    meta: { touched, error, submitError },
  } = useField(name, { validate });

  const selected = multiple
    ? options.filter((opt) => Array.isArray(value) && value.includes(opt.value))
    : freeSolo
      ? options.find((opt) => opt.value === value) || value || null
      : options.find((opt) => opt.value === value) || null;

  /**
   * `Autocomplete` porovnáva `value` **identitou** a pri každej zmene si prepíše
   * rozpísaný vstup (efekt nad `resetInputValue` v `useAutocomplete`). Pri
   * `multiple` je `value` pole, ktoré tu vzniká z `options` - teda nové pri
   * každom renderi - takže sa vstup po prvom znaku sám premazal a hľadať sa
   * nedalo. Identita sa preto mení až s **obsahom** výberu.
   */
  const selectedSignature = JSON.stringify(selected);
  const stableSelected = React.useMemo(
    () => selected,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedSignature],
  );

  const handleListboxScroll = React.useCallback(
    (event: React.UIEvent<HTMLElement>) => {
      if (!onLoadMore || !hasMore || loading) return;

      const listbox = event.currentTarget;
      // 24 px rezerva, aby sa ďalšia stránka začala ťahať ešte pred samým
      // koncom a scroll nezasekol na „už nič nie je".
      if (
        listbox.scrollTop + listbox.clientHeight >=
        listbox.scrollHeight - 24
      ) {
        onLoadMore();
      }
    },
    [onLoadMore, hasMore, loading],
  );

  return (
    <Autocomplete
      freeSolo={freeSolo}
      slotProps={
        onLoadMore ? { listbox: { onScroll: handleListboxScroll } } : undefined
      }
      clearOnEscape
      filterOptions={disableClientFilter ? (opts: Option[]) => opts : undefined}
      multiple={multiple}
      options={options}
      noOptionsText={noOptionsText}
      fullWidth={fullWidth}
      disabled={disabled}
      value={stableSelected}
      onChange={(_, newValue) => {
        if (multiple) {
          onChange(Array.isArray(newValue) ? newValue.map((v) => v.value) : []);
        } else {
          onChange(
            newValue && typeof newValue === 'object' && !Array.isArray(newValue)
              ? newValue.value
              : typeof newValue === 'string'
                ? newValue
                : null,
          );
        }
      }}
      onInputChange={onInputChange}
      onBlur={onBlur}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return Array.isArray(option) ? '' : option.label;
      }}
      isOptionEqualToValue={(option, val) => {
        if (Array.isArray(option) || Array.isArray(val)) return false;
        return option.value === val.value;
      }}
      renderInput={(params) => (
        <FieldShell
          label={label}
          htmlFor={`id-${name}`}
          required={required}
          info={info}
          error={(touched ? error || submitError : submitError) || null}
        >
          <TextField
            {...params}
            id={`id-${name}`}
            error={!!(touched ? error || submitError : submitError)}
            sx={{
              // Vstup je `type='search'` (viď `htmlInput` nižšie), takže by
              // doň WebKit pridal vlastný krížik na vymazanie - Autocomplete
              // má svoj.
              '& input::-webkit-search-cancel-button': {
                display: 'none',
              },
              '& input::-webkit-search-decoration': {
                WebkitAppearance: 'none',
              },
            }}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color='inherit' size={20} />
                    ) : null}
                    {touched && (error || submitError) ? (
                      <ErrorMark message={error || submitError} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
              htmlInput: {
                ...params.inputProps,
                ...searchInputProps,
                'aria-describedby': (
                  touched ? error || submitError : submitError
                )
                  ? `id-${name}-err`
                  : undefined,
              },
            }}
            size='small'
          />
        </FieldShell>
      )}
      renderValue={
        multiple
          ? (selected, getItemProps) =>
              Array.isArray(selected)
                ? selected.map((option, index) =>
                    !Array.isArray(option) ? (
                      <MuiChip
                        size='small'
                        label={option.label}
                        {...getItemProps({ index })}
                        key={option.value}
                      />
                    ) : null,
                  )
                : null
          : undefined
      }
    />
  );
};

export default FormAutocompleteField;
